import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import AccountUpgradeModal from '../AccountUpgradeModal';

interface AuthenticatedLayoutProps {
  children: React.ReactNode;
  loadingMessage?: string;
  accessDeniedTitle?: string;
  accessDeniedMessage?: string;
}

const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({
  children,
  loadingMessage = "Loading...",
  accessDeniedTitle = "Access Denied",
  accessDeniedMessage = "Please sign in to access this page."
}) => {
  const { user, loading: authLoading } = useAuth();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  if (authLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-gray-600">{loadingMessage}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">{accessDeniedTitle}</h3>
        <p className="text-gray-500">{accessDeniedMessage}</p>
      </div>
    );
  }

  const isGuestUser = user.isAnonymous;

  return (
    <>
      {/* Guest Account Alert Banner */}
      {isGuestUser && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-amber-400 text-xl">⚠️</span>
              </div>
              <div className="ml-3">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">Guest Account</span>
                  <br />
                  You're using a guest account. Consider upgrading to save your data permanently and access additional features.
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={() => setIsUpgradeModalOpen(true)}
                className="bg-amber-600 text-white px-3 py-1 rounded text-sm font-medium hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Content */}
      {children}

      {/* Account Upgrade Modal */}
      <AccountUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
      />
    </>
  );
};

export default AuthenticatedLayout; 